import React, { useEffect, useState, useMemo, useRef } from 'react'
import useManageOrder from '../features/order/useManageOrder';
import OrderService from '../features/order/OrderService';
import {
  appendOrderData,
  buildOrderReceipts,
  hasAnySelection,
  hasNullAmountSelected,
  applyWeightColumnToggle,
  applyDeliveryFeeThColumnToggle,
  buildSelectionMapFromReceipts,
  applySelectionMap,
} from './paymentFormUtils';
import { formatDate } from '../utils/dateUtils';
import { formatAmount, formatAmountRound } from '../utils/formatAmount';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import { toast } from 'react-toastify';
import { WeightAssistTag, useWeightAssists } from '../features/caseTicket/WeightAssistTag';
import checkReceipt from '../utils/checkReceipt';
import readQrData from '../utils/readQrData';
import Modal from 'react-modal';
import ReceiptNotMatchModal from '../features/keyData/ReceiptNotMatchModal';
import ReceiptPasteModal from '../features/keyData/ReceiptPasteModal';
import useCustomerProfile from '../features/customerProfile/useCustomerProfile';
import checkBlacklistResultAfterPaid from '../utils/checkBlacklistResultAfterPaid';
import { useSelector, useDispatch } from 'react-redux';
import { selectCustomerProfiles, getCustomerProfiles } from '../redux/features/customerProfileSlice';
import OrderSummaryTable from './OrderSummaryTable';
import PaymentTextArea from './PaymentTextArea';
import { getUnpaidExtraJpFeesThb } from '../utils/extraJpFees';
import TextField from '@mui/material/TextField';
import useBankTransferOnly from '../hooks/useBankTransferOnly';
import { COMPANY_BANK_NOTICE } from '../utils/companyBank';


const customStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)', // This sets the overlay color
    zIndex:2000
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    width: '500px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};

// Wider variant for the declaration modal (long urls need room).
const declarationStyles = {
  overlay: customStyles.overlay,
  content: { ...customStyles.content, width: '760px', maxWidth: '92vw', maxHeight: '85vh' },
};

// Prominent air/ship badge so admins can tell the round type at a glance.
const shipTypeBadge = (isAir) => (
  <span style={{
    marginLeft: 8,
    padding: '1px 8px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    background: isAir ? '#e0f2fe' : '#dcfce7',
    color: isAir ? '#0369a1' : '#15803d',
  }}>
    {isAir ? '✈️ แอร์' : '🚢 เรือ'}
  </span>
);

const ShipDatePayment = ({smileName, refreshPendingOrder, onClose}) => {
  
  const dispatch = useDispatch()
  const {orders,
    setOrders,
    getOrdersFromSmileName,
    getPaymentIntentSummary,
    createNonCompanySummaryCode,
    getSummaryByCode,
  } = useManageOrder()
  // ตามของ weight-fee discounts on these orders (label in the weight cell)
  const weightAssists = useWeightAssists((orders || []).map((o) => o.id));

  const profiles = useSelector(selectCustomerProfiles)
  const shopName = useSelector((state) => state.shop.name);

  const [rowData, setRowData] = useState([]);
  const [files, setFiles] = useState(null);
  const [totalThAmount, setTotalThAmount] = useState(0)
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [shipDateIds, setShipDateIds] = useState(['All'])
  const [isLoading, setIsLoading] = useState(false);
  const [isCreditPaid, setIsCreditPaid] = useState(false)
  const [receipValueState, setReceiptValueState] = useState('')
  const [issueFormData, setIssueFormData] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [shipDates, setShipDates] = useState([])
  const [isPastingModalOpen, setIsPastingModalOpen] = useState(false);
  const [profileState, setProfileState] = useState({})
  // While links are off, company customers pay group 1 into the company account
  const bankTransferOnly = useBankTransferOnly()
  const {updateOrderAndUploadReceipt, error: orderError} = useManageOrder()
  const [showPaymentTextArea, setShowPaymentTextArea] = useState(false);
  const paymentTextAreaRef = useRef(null);
  // Stores selections restored by handleCodeLookup so they survive a filteredOrders reset
  const pendingCodeSelectionsRef = useRef(null);
  const [testOrderSummary, setTestOrderSummary] = useState(null);
  const [nonCompanySummary, setNonCompanySummary] = useState(null);
  const [codeInput, setCodeInput] = useState('');
  const [isDeclarationOpen, setIsDeclarationOpen] = useState(false);

  /** Credit applied to this payment batch — capped at selected total so remainder stays on profile */
  const advanceToApply = useMemo(() => {
    const adv = Number(profileState?.advanceAmount);
    const total = Number(totalThAmount);
    const safeAdv = Number.isFinite(adv) && adv > 0 ? adv : 0;
    const safeTotal = Number.isFinite(total) && total >= 0 ? total : 0;
    return Math.min(safeAdv, safeTotal);
  }, [profileState?.advanceAmount, totalThAmount]);

  const netDueAfterAdvance = useMemo(
    () => Math.max(0, (Number(totalThAmount) || 0) - advanceToApply),
    [totalThAmount, advanceToApply]
  );

  // The advance "covers" the selected items when it's within 1 baht of the total — matching the
  // backend's 1-baht settlement tolerance (orderPaymentController isAdvanceAmountSettlement). A
  // strict netDueAfterAdvance === 0 check blocked settlements that were a fraction of a baht
  // short from decimal rounding, so the same case worked or failed depending on the numbers.
  const canSettleWithAdvance = useMemo(
    () => advanceToApply > 0 && netDueAfterAdvance < 1,
    [advanceToApply, netDueAfterAdvance]
  );

  // Declaration (ใบแจ้งรายละเอียด): plain-language list of what the customer is paying
  // for per item — categories only, no amounts. Built from the currently selected rows,
  // so it works for company and non-company alike with no code needed.
  // Half payments are noted: 'half' = paying the first 50% now; 'full' on an
  // already-half-paid order = paying the remaining 50%.
  const paymentPartNote = (selected, alreadyHalfPaid) => {
    if (selected === 'half') return ' (50% แรก)'
    if (selected === 'full' && alreadyHalfPaid) return ' (50% หลัง)'
    return ''
  }
  const declarationCategories = (order) => {
    const cats = []
    const productSelected = order.amountSelected && order.amountSelected !== 'none'
    if (productSelected) {
      cats.push(`ค่าสินค้า${paymentPartNote(order.amountSelected, order.productPayment === 'มัดจำ 50%')}`)
      // Collected together with the product payment (mirrors the expected-total math):
      // transaction fee rides 'half' and full-first payments; ค่าตรวจ rides any product
      // selection while still unpaid.
      const feeCollectedNow = order.amountSelected === 'half' ||
        (order.amountSelected === 'full' && order.productPayment !== 'มัดจำ 50%')
      if (feeCollectedNow && (order.transactionFee || 0) > 0 && !order.transactionFeePaid)
        cats.push('ค่าบริการ')
      if (getUnpaidExtraJpFeesThb(order) > 0)
        cats.push('ค่าตรวจ')
    }
    if (order.deliveryFeeJpSelected && order.deliveryFeeJpSelected !== 'none')
      cats.push(`ค่าส่งในญี่ปุ่น${paymentPartNote(order.deliveryFeeJpSelected, order.shipJpPaid === 'มัดจำ 50%')}`)
    if (order.weightPriceSelected) cats.push('ค่าน้ำหนัก')
    if (order.deliveryFeeThSelected) cats.push('ค่าส่งกลับไทย')
    return cats
  }
  const buildDeclarationText = () => {
    const selected = rowData.filter((o) => declarationCategories(o).length > 0)
    if (selected.length === 0) return ''
    let t = `📋 ใบแจ้งรายละเอียดการชำระ\n\n`
    selected.forEach((o, i) => {
      t += `${i + 1}) ${o.url}\n   ✅ ${declarationCategories(o).join(', ')}\n\n`
    })
    return t.trimEnd()
  }

  const handleCheckboxChange = () => {
    setIsCreditPaid(!isCreditPaid);
  };

  const {checkAndRemoveBlacklistIfNeeded} = useCustomerProfile();

  const handleIssueSave = async (note) => {
    setIsLoading(true)
    issueFormData.append('receiptIssue',true)
    issueFormData.append('note',note)
    if (advanceToApply > 0) {
      issueFormData.append('advanceAmount', advanceToApply);
    }
    issueFormData.append('frontendSource', 'ShipDatePayment');
    OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'updateOrderAndUploadReceipt', smileName, shop: shopName, detail: 'issue save' });
    const response = await updateOrderAndUploadReceipt(issueFormData)
    setIsLoading(false)
    if(response === true){
      await checkBlacklistResultAfterPaid(profileState, checkAndRemoveBlacklistIfNeeded)
      toast.success("Update Successful!!")
      setIssueFormData('')
      refreshPendingOrder()
      setIsModalOpen(false)
      handleFinishUpdate()
    }else{
      const errorMessage = response || 'เกิดข้อผิดพลาดในการอัพเดทข้อมูล';
      toast.error(errorMessage);
      alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง`);
    }
  }

  const closeModal = () => {
    OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'receipt_not_match_modal_closed_without_save', smileName, shop: shopName, detail: { note: 'admin left without completing Update' } });
    setIsModalOpen(false)
  }

  const handleFileChange = (event) => {
    console.log("handle file change")
    setFiles([...event.target.files]);
  };

  const handleShipDateSelectChange = (e) => {
    const value = e.target.value;
    // MUI multiple select returns an array
    let newValue = typeof value === 'string' ? value.split(',') : value;

    // If 'All' was just selected (last item), reset to only 'All'
    if (newValue.includes('All') && !shipDateIds.includes('All')) {
      newValue = ['All'];
    }
    // If something else was selected while 'All' was active, remove 'All'
    else if (newValue.includes('All') && newValue.length > 1) {
      newValue = newValue.filter(v => v !== 'All');
    }
    // If nothing is selected, default back to 'All'
    else if (newValue.length === 0) {
      newValue = ['All'];
    }

    setShipDateIds(newValue);
  }

  const handleConfirmPastedImage = (file) => {
    setFiles(file);
    const fileCount = Array.isArray(file) ? file.length : (file ? 1 : 0);
    OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'receipt_paste_modal_confirmed_with_file', smileName, shop: shopName, detail: { fileCount } });
  };

  const filterOrders = (selectedIds) => {
    if (selectedIds.includes('All')) {
      setFilteredOrders(orders); // If 'All' is selected, show all orders
    } else {
      const filtered = orders.filter(order => {
        if (selectedIds.includes('None') && order.shipDateId == null) return true;
        if (selectedIds.includes(order.shipDateId)) return true;
        return false;
      });
      setFilteredOrders(filtered);
    }
  };

  const handleFinishUpdate = async () => {
    await getOrdersFromSmileName({smileName, paymentStatus:'paymentNotComplete'});
  }

  useEffect(() => {
    const extractUniqueShipDatesWithIds = (orders) => {
      const uniqueDates = {};
      orders.forEach((order) => {
        if (order.ShipDate) {
          const shipDate = order.ShipDate.shipDate;
          // If the shipDate is not already in uniqueDates, add it
          if (!uniqueDates[shipDate]) {
            uniqueDates[shipDate] = { id: order.ShipDate.id, shipDate, isAir: order.ShipDate.isAir };
          }
        }
      });
      // Convert the uniqueDates object to an array and sort it by id in descending order
      return Object.values(uniqueDates).sort((a, b) => b.id - a.id);
    };
  
    const uniqueShipDatesWithIds = extractUniqueShipDatesWithIds(orders);
    setShipDates(uniqueShipDatesWithIds);
    console.log(uniqueShipDatesWithIds);
  }, [orders]);

  useEffect(() => {
    getOrdersFromSmileName({smileName, paymentStatus:'paymentNotComplete'});
  }, []);

  useEffect(() => {
    if (showPaymentTextArea && paymentTextAreaRef.current) {
      setTimeout(() => paymentTextAreaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [showPaymentTextArea]);

  useEffect(() => {
    const findProfile = () => {
      const profile = profiles.find(profile => profile.smileName === smileName);
      if (profile) {
        setProfileState(profile);
      } else {
        setProfileState(null);
      }
    };
    findProfile();
  }, [smileName, profiles]);

  useEffect(() => {
    const formattedOrders = filteredOrders.map(order => ({
      ...order,
      orderDate: formatDate(order.orderDate),
      amountSelected: "none",
      deliveryFeeJpSelected: "none",
      weightPriceSelected: false,
      deliveryFeeThSelected: false
    }));
    const pending = pendingCodeSelectionsRef.current;
    if (pending) {
      pendingCodeSelectionsRef.current = null;
      setRowData(applySelectionMap(formattedOrders, pending));
    } else {
      setRowData(formattedOrders);
    }
  }, [filteredOrders]);

  const cyclePaymentState = (current, item, dataPoint) => {
    // If already half-paid, only allow "none" <-> "full"
    const isAlreadyHalfPaid =
      (dataPoint === 'amountSelected' && item.productPayment === 'มัดจำ 50%') ||
      (dataPoint === 'deliveryFeeJpSelected' && item.shipJpPaid === 'มัดจำ 50%');

    if (isAlreadyHalfPaid) {
      // Only cycle between "none" and "full"
      return current === "none" ? "full" : "none";
    } else {
      // Normal 3-state cycle: none -> full -> half -> none
      if (current === "none") return "full";
      if (current === "full") return "half";
      return "none";
    }
  };

  const toggleSelection = (rowIndex, dataPoint) => {
    setRowData(currentData =>
      currentData.map((item, index) => {
        if (index !== rowIndex) return item;

        // Prevent toggling if already fully paid
        if (
          (dataPoint === 'amountSelected' && item.productPayment === 'ชำระครบแล้ว') ||
          (dataPoint === 'deliveryFeeJpSelected' && item.shipJpPaid === 'ชำระครบแล้ว') ||
          (dataPoint === 'weightPriceSelected' && item.weightPaid) ||
          (dataPoint === 'deliveryFeeThSelected' && item.shipThPaid) ||
          // A fee not entered yet can't be paid (same rule as the column toggle)
          (dataPoint === 'weightPriceSelected' && item.weightPrice == null && item.weight == null) ||
          (dataPoint === 'deliveryFeeThSelected' && item.deliveryFeeTh == null)
        ) {
          return item;
        }

        // 3-state or 2-state cycle for amountSelected and deliveryFeeJpSelected
        if (dataPoint === 'amountSelected' || dataPoint === 'deliveryFeeJpSelected') {
          return { ...item, [dataPoint]: cyclePaymentState(item[dataPoint], item, dataPoint) };
        }

        // 2-state toggle for weightPriceSelected and deliveryFeeThSelected
        if (dataPoint === 'weightPriceSelected' || dataPoint === 'deliveryFeeThSelected') {
          return { ...item, [dataPoint]: !item[dataPoint] };
        }

        return item;
      })
    );
  };

  const handlePastingOpenModal = () => {
    setIsPastingModalOpen(true);
    OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'receipt_paste_modal_opened', smileName, shop: shopName, detail: {} });
  };

  const handlePastingCloseModal = () => {
    const hadFile = !!(files && (Array.isArray(files) ? files.length : true));
    OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'receipt_paste_modal_closed', smileName, shop: shopName, detail: { hadFile } });
    setIsPastingModalOpen(false);
  };

  const checkReceiptValue = async (formData) => {
    try {
      let totalSum = 0;
      let errorStatus = false
      // Read all QR codes in parallel
      const qrCodePromises = files.map(file => readQrData(file).catch(error => {
        const errorMessage = error || 'ไม่สามารถอ่าน QR Code จากสลิปได้';
        toast.error(errorMessage);
        alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบสลิปและลองใหม่อีกครั้ง`);
        errorStatus = true
        return null; // Return null or an appropriate value if QR code read fails
      }));
      const qrCodes = await Promise.all(qrCodePromises);
  
      // Prepare receipt check promises
      const receiptPromises = qrCodes.map((qrCodeData, index) => {
        if (qrCodeData) {
          return checkReceipt(qrCodeData).then(receiptData => { 
            if (receiptData) {
              formData.append('accountNo', receiptData.data.receiver.account.value);
              formData.append('amount', receiptData.data.amount);
              formData.append('paymentDateTime', receiptData.data.transTimestamp);
              totalSum += parseFloat(receiptData.data.amount);
            } else {
              // checkReceipt resolved but returned no data — treat as read failure
              const errorMessage = 'ไม่สามารถอ่านข้อมูลจากสลิปได้ กรุณาตรวจสอบสลิปอีกครั้ง';
              toast.error(errorMessage);
              alert(`⚠️ ข้อผิดพลาด: ${errorMessage}`);
              errorStatus = true;
            }
          }).catch(error => {
            const errorMessage = error || 'ไม่สามารถตรวจสอบสลิปได้';
            toast.error(errorMessage);
            alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบสลิปและลองใหม่อีกครั้ง`);
            errorStatus = true;
          });
        }
      });
  
      // Wait for all receipt checks to complete
      await Promise.all(receiptPromises);
  
      return { totalSum, formData, errorStatus };
    } catch (error) {
      setIsLoading(false);
      console.error("Error processing receipts:", error);
      const errorMessage = error?.message || 'เกิดข้อผิดพลาดในการประมวลผลสลิป';
      toast.error(errorMessage);
      alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาลองใหม่อีกครั้ง`);
      return { totalSum: 0, formData, errorStatus: true };
    }
  }

  const handleUpdatePayment = async () => {  
    OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'submit_button_pressed', smileName, shop: shopName, detail: { hasFiles: !!(files && (Array.isArray(files) ? files.length : true)), ordersCount: rowData.length } });
    setIsLoading(true)
    try {
      // Credit-only settlement: the customer's on-file credit fully covers the selected
      // items (net ≤ 0), so no money is transferred and there is no slip to attach.
      // Mark the selected orders paid using advanceAmount via the backend isAdvanceAmountSettlement path.
      if(canSettleWithAdvance){
        // This path sends NO files — a slip attached here would be silently discarded
        // (backend skips the receipt pipeline for credit-only settlements). If the admin
        // attached one, they almost certainly expect it recorded: make them choose.
        if(files && (Array.isArray(files) ? files.length : true)){
          const errorMessage = 'ยอดฝากครอบคลุมรายการที่เลือกทั้งหมด — ไม่ต้องใช้สลิป และสลิปที่แนบจะไม่ถูกบันทึก\nกรุณาลบสลิปที่แนบออกก่อนตัดยอด หรือเลือกรายการเพิ่มถ้าลูกค้าโอนเงินมาเพิ่ม';
          toast.error('มีสลิปแนบอยู่ — การตัดยอดด้วยยอดฝากไม่บันทึกสลิป');
          alert(`⚠️ ${errorMessage}`);
          OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'credit_settlement_blocked_files_attached', smileName, shop: shopName, detail: { advanceToApply } });
          setIsLoading(false);
          return;
        }
        if(!hasAnySelection(rowData)){
          toast.error('กรุณาเลือกรายการที่ต้องการชำระก่อน');
          setIsLoading(false);
          return;
        }
        if(hasNullAmountSelected(rowData)){
          const errorMessage = "มีบางรายการยังไม่ได้ ลงยอดค่าใช้จ่าย";
          toast.error(errorMessage);
          alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบรายการที่เลือก`);
          setIsLoading(false);
          return;
        }
        const formData = new FormData();
        formData.append('isShipDatePayment', 'true');
        formData.append('isAdvanceAmountSettlement', 'true');
        formData.append('smileName', smileName);
        formData.append('advanceAmount', advanceToApply);
        formData.append('frontendSource', 'ShipDatePayment');
        const creditFormData = appendOrderData(formData, rowData);
        OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'updateOrderAndUploadReceipt', smileName, shop: shopName, detail: 'advance-amount settlement (no slip)' });
        const response = await updateOrderAndUploadReceipt(creditFormData);
        setIsLoading(false);
        if(response == true){
          await checkBlacklistResultAfterPaid(profileState, checkAndRemoveBlacklistIfNeeded);
          await dispatch(getCustomerProfiles({ shop: shopName, force: true }));
          toast.success("Update Successful!!");
          handleFinishUpdate();
        }else{
          const errorMessage = response || 'เกิดข้อผิดพลาดในการอัพเดทข้อมูล';
          toast.error(errorMessage);
          alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง`);
        }
        return;
      }

      if(files){
        if (!hasAnySelection(rowData)) {
          toast.error('กรุณาเลือกรายการที่ต้องการชำระก่อน');
          setIsLoading(false);
          return;
        }
        const hasNullValue = hasNullAmountSelected(rowData);
        console.log(hasNullValue)
        if(!hasNullValue){
          const formData = new FormData();
          formData.append('isShipDatePayment', true)
          formData.append('smileName', smileName)
          files.forEach((file) => {
            formData.append('images', file);
          });
          const orderAddedFormData = appendOrderData(formData, rowData)
          if(isCreditPaid){ //Case Credit Card
            orderAddedFormData.append('accountNo', 'Shipnity');
            orderAddedFormData.append('amount', netDueAfterAdvance);
            orderAddedFormData.append('paymentDateTime', new Date());
            orderAddedFormData.append('receiptIssue', true);
            orderAddedFormData.append('isCreditPaid', true);
            if (advanceToApply > 0) {
              orderAddedFormData.append('advanceAmount', advanceToApply);
            }
            orderAddedFormData.append('frontendSource', 'ShipDatePayment');
            OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'updateOrderAndUploadReceipt', smileName, shop: shopName, detail: 'credit card' });
            const response = await updateOrderAndUploadReceipt(orderAddedFormData)
            if(response == true){
              await checkBlacklistResultAfterPaid(profileState, checkAndRemoveBlacklistIfNeeded)
              await dispatch(getCustomerProfiles({ shop: shopName, force: true })) // Refresh profiles to get updated advanceAmount
              toast.success("Update Successful!!")
              handleFinishUpdate()
            }else {
              const errorMessage = response || 'เกิดข้อผิดพลาดในการอัพเดทข้อมูล';
              toast.error(errorMessage);
              alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง`);
            }
             setIsLoading(false)
          }else{
            const receiptData = await checkReceiptValue(orderAddedFormData)
            if(!receiptData.errorStatus){
              const receiptValue = receiptData.totalSum
              const combinedFormData = receiptData.formData
              setReceiptValueState(receiptValue)
              if (receiptValue && Math.abs(receiptValue - netDueAfterAdvance) < 10) {
                console.log(receiptValue)
                console.log("DATA IS PASS")
                if (advanceToApply > 0) {
                  combinedFormData.append('advanceAmount', advanceToApply);
                }
                combinedFormData.append('frontendSource', 'ShipDatePayment');
                OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'updateOrderAndUploadReceipt', smileName, shop: shopName, detail: 'receipt match' });
                const response = await updateOrderAndUploadReceipt(combinedFormData)
                setIsLoading(false)
                if(response == true){
                  await checkBlacklistResultAfterPaid(profileState, checkAndRemoveBlacklistIfNeeded)
                  await dispatch(getCustomerProfiles({ shop: shopName, force: true })) // Refresh profiles to get updated advanceAmount
                  handleFinishUpdate()
                  toast.success("Update Successful!!")
                }else{
                  const errorMessage = orderError || response || 'เกิดข้อผิดพลาดในการอัพเดทข้อมูล';
                  toast.error(errorMessage);
                  alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง`);
                }
              } else {
                OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'submit_amount_not_match', smileName, shop: shopName, detail: { receiptValue, totalThAmount, advanceToApply, netDueAfterAdvance, diff: Math.abs(receiptValue - netDueAfterAdvance) } });
                setIsModalOpen(true)
                setIssueFormData(combinedFormData)
                setIsLoading(false)
              }
            }else{
              OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'receipt_read_error' } });
              const errorMessage = 'เกิดข้อผิดพลาดในการอ่านสลิป กรุณาตรวจสอบสลิปและลองใหม่อีกครั้ง';
              toast.error(errorMessage);
              alert(`⚠️ ข้อผิดพลาด: ${errorMessage}`);
              setIsLoading(false)
            }
          }
        }else{
          OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'some_amounts_not_entered' } });
          const errorMessage = "มีบางรายการยังไม่ได้ ลงยอดค่าใช้จ่าย";
          toast.error(errorMessage);
          alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบรายการที่เลือก`);
          setIsLoading(false)
        }
      }else{
        OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'submit_validation_fail', smileName, shop: shopName, detail: { reason: 'no_receipt_attached' } });
        setIsLoading(false)
        const errorMessage = "กรุณาแนบสลิป";
        toast.error(errorMessage);
        alert(`⚠️ ข้อผิดพลาด: ${errorMessage}`);
      }
    } catch (error) {
      setIsLoading(false);
      const errorMessage = error?.response?.data?.message || error?.message || 'เกิดข้อผิดพลาดที่ไม่คาดคิด';
      OrderService.logFrontendEvent({ source: 'ShipDatePayment', action: 'unexpected_error', smileName, shop: shopName, detail: { error: errorMessage } });
      toast.error(errorMessage);
      alert(`⚠️ เกิดข้อผิดพลาด: ${errorMessage}\n\nกรุณาลองใหม่อีกครั้ง และตรวจสอบว่าข้อมูลถูกบันทึกหรือไม่`);
    }
  }

  const handlePaymentIntent = async () => {
    setIsLoading(true)
    try {
      if (hasNullAmountSelected(rowData)) {
        const errorMessage = 'กรุณากรอกจำนวนเงินให้ครบถ้วน';
        toast.error(errorMessage);
        alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาตรวจสอบข้อมูลที่กรอก`);
        setIsLoading(false)
        return
      }

      const orderReceipts = buildOrderReceipts(rowData)

      if (!profileState?.isCompanyCustomer) {
        // Non-company: generate code + bank transfer summary
        const result = await createNonCompanySummaryCode({ smileName, orderReceipts })
        if (result) {
          setNonCompanySummary(result)
          setShowPaymentTextArea(true)
          toast.success('สร้าง สรุปรายการ สำเร็จ')
        } else {
          const errorMessage = 'เกิดข้อผิดพลาดในการสร้าง สรุปรายการ';
          toast.error(errorMessage);
          alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาลองใหม่อีกครั้ง`);
        }
        return
      }

      // Company customer: original flow with payment links
      const requestData = {
        smileName: smileName,
        orderReceipts: orderReceipts,
        paymentMethod: isCreditPaid ? 'creditCard' : 'qrCode',
        ...(isCreditPaid && { allCreditCard: true })
      }

      const result = await getPaymentIntentSummary(requestData)

      if (result) {
        setTestOrderSummary(result);
        setShowPaymentTextArea(true);
        toast.success('สร้าง สรุปรายการ สำเร็จ')
      } else {
        const errorMessage = 'เกิดข้อผิดพลาดในการสร้าง สรุปรายการ';
        toast.error(errorMessage);
        alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาลองใหม่อีกครั้ง`);
      }

    } catch (error) {
      console.error('สรุปรายการ Error:', error)
      const errorMessage = error?.message || 'เกิดข้อผิดพลาดในการสร้าง สรุปรายการ';
      toast.error(errorMessage);
      alert(`⚠️ ข้อผิดพลาด: ${errorMessage}\n\nกรุณาลองใหม่อีกครั้ง`);
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeLookup = async () => {
    const trimmed = codeInput.trim().toUpperCase()
    if (!trimmed) return
    setIsLoading(true)
    try {
      const result = await getSummaryByCode(trimmed)
      if (!result) {
        toast.error('ไม่พบ Code นี้ในระบบ')
        return
      }
      if (result.smileName !== smileName) {
        toast.error('Code นี้ไม่ตรงกับลูกค้ารายนี้')
        return
      }
      const selectionMap = buildSelectionMapFromReceipts(result.orderReceipts)
      // Store in ref so the filteredOrders reset effect can re-apply selections if
      // getOrdersFromSmileName resolves after this code lookup (race condition).
      pendingCodeSelectionsRef.current = selectionMap
      setRowData(currentData => applySelectionMap(currentData, selectionMap))
      toast.success(`โหลด Code ${trimmed} สำเร็จ — เลือกรายการอัตโนมัติแล้ว`)
    } catch (error) {
      toast.error('เกิดข้อผิดพลาดในการค้นหา Code')
    } finally {
      setIsLoading(false)
    }
  }

  const toggleAllRows = () => {
    setRowData(currentData => {
      // If any row is not "full", select all as "full", else set all to "none"
      const anyNotFull = currentData.some(item =>
        (item.productPayment !== 'ชำระครบแล้ว' && item.amountSelected !== "full") ||
        (item.shipJpPaid !== 'ชำระครบแล้ว' && item.deliveryFeeJpSelected !== "full") ||
        (!item.weightPaid && (item.weightPrice != null || item.weight != null) && !item.weightPriceSelected) ||
        (!item.shipThPaid && item.deliveryFeeTh != null && !item.deliveryFeeThSelected)
      );
      return currentData.map(item => ({
        ...item,
        amountSelected: item.productPayment !== 'ชำระครบแล้ว'
          ? (anyNotFull ? "full" : "none")
          : "none",
        deliveryFeeJpSelected: item.shipJpPaid !== 'ชำระครบแล้ว'
          ? (anyNotFull ? "full" : "none")
          : "none",
        weightPriceSelected: !item.weightPaid && (item.weightPrice != null || item.weight != null) ? anyNotFull : false,
        deliveryFeeThSelected: !item.shipThPaid && item.deliveryFeeTh != null ? anyNotFull : false
      }));
    });
  };


  const selectAllInRow = (rowIndex) => {
    setRowData(currentData =>
      currentData.map((item, index) => {
        if (index !== rowIndex) return item;

        // Determine if this row is currently not selected (for any field)
        const anyNotFull =
          (item.productPayment !== 'ชำระครบแล้ว' && item.amountSelected !== "full") ||
          (item.shipJpPaid !== 'ชำระครบแล้ว' && item.deliveryFeeJpSelected !== "full") ||
          (!item.weightPaid && (item.weightPrice != null || item.weight != null) && !item.weightPriceSelected) ||
          (!item.shipThPaid && item.deliveryFeeTh != null && !item.deliveryFeeThSelected);

        return {
          ...item,
          amountSelected: item.productPayment !== 'ชำระครบแล้ว'
            ? (anyNotFull ? "full" : "none")
            : "none",
          deliveryFeeJpSelected: item.shipJpPaid !== 'ชำระครบแล้ว'
            ? (anyNotFull ? "full" : "none")
            : "none",
          weightPriceSelected: !item.weightPaid && (item.weightPrice != null || item.weight != null) ? anyNotFull : false,
          deliveryFeeThSelected: !item.shipThPaid && item.deliveryFeeTh != null ? anyNotFull : false
        };
      })
    );
  };

  const toggleColumn = (dataPoint) => {
    setRowData(currentData => {
      if (dataPoint === 'weightPriceSelected') return applyWeightColumnToggle(currentData);
      if (dataPoint === 'deliveryFeeThSelected') return applyDeliveryFeeThColumnToggle(currentData);

      const anyNotSelected = currentData.some(item => {
        if (dataPoint === 'amountSelected') {
          return item.productPayment !== 'ชำระครบแล้ว' && item.amountSelected !== "full";
        } else if (dataPoint === 'deliveryFeeJpSelected') {
          return item.shipJpPaid !== 'ชำระครบแล้ว' && item.deliveryFeeJpSelected !== "full";
        }
        return false;
      });

      return currentData.map(item => {
        if (
          (dataPoint === 'amountSelected' && item.productPayment === 'ชำระครบแล้ว') ||
          (dataPoint === 'deliveryFeeJpSelected' && item.shipJpPaid === 'ชำระครบแล้ว')
        ) {
          return item;
        }
        return { ...item, [dataPoint]: anyNotSelected ? "full" : "none" };
      });
    });
  };

  useEffect(() => {
    filterOrders(shipDateIds);
  }, [shipDateIds, orders]);

  useEffect(() => {
    const total = rowData.reduce((acc, order) => {
      // First-half deposit = 50% + downRate% service fee — per-customer, stored on the
      // order (falls back to 3, the payment-link default). Was hardcoded 0.53, which
      // undercharged/overcharged customers whose downRate ≠ 3.
      const downFactor = 0.5 + ((order.downRate ?? 3) / 100);
      // Product Payment
      let productPay = 0;
      if (order.amountSelected === "half") {
        productPay = order.amount * order.qty * downFactor * order.productRate + (order.transactionFee || 0);
      } else if (order.amountSelected === "full") {
        if (order.productPayment === 'มัดจำ 50%') {
          productPay = order.amount * order.qty * 0.5 * order.productRate;
        } else {
          productPay = order.amount * order.qty * order.productRate + (order.transactionFee || 0);
        }
      }
      // ค่าตรวจ (extra JP fees) is collected together with the product payment —
      // the unpaid fee joins whenever the product component is selected (0 once paid,
      // so a มัดจำ second half whose fee was already collected adds nothing)
      if (order.amountSelected === "half" || order.amountSelected === "full") {
        productPay += getUnpaidExtraJpFeesThb(order);
      }

      // Delivery Fee JP
      let deliveryFeeJpPay = 0;
      if (order.deliveryFeeJpSelected === "half") {
        deliveryFeeJpPay = (order.deliveryFeeJp || 0) * downFactor * (order.isCod ? order.codRate : order.productRate);
      } else if (order.deliveryFeeJpSelected === "full") {
        if (order.shipJpPaid === 'มัดจำ 50%') {
          deliveryFeeJpPay = (order.deliveryFeeJp || 0) * 0.5 * (order.isCod ? order.codRate : order.productRate);
        } else {
          deliveryFeeJpPay = (order.deliveryFeeJp || 0) * (order.isCod ? order.codRate : order.productRate);
        }
      }

      // Weight and Thai Fee (only full or none)
      // For isSendOnly orders, minimum weight is 1kg (use weightRate if weight < 1)
      let weightPay = 0;
      if (order.weightPriceSelected) {
        if (order.weightPrice) {
          weightPay = order.weightPrice;
        } else if (order.weight && order.weightRate) {
          weightPay = order.isSendOnly && order.weight < 1 
            ? order.weightRate 
            : order.weight * order.weightRate;
        }
      }
      let deliveryFeeThPay = order.deliveryFeeThSelected ? order.deliveryFeeTh : 0;

      return acc + productPay + deliveryFeeJpPay + weightPay + deliveryFeeThPay;
    }, 0);
  
    setTotalThAmount(total);
  }, [rowData]);
  
  return (
    <div style={{ position: 'relative' }}>
      {onClose && (
        <div className="sticky top-4 z-50 flex justify-end" style={{ zIndex: 9999 }}>
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded shadow-lg"
          >
            ✕ Close
          </button>
        </div>
      )}
      <div className='flex justify-between'>
        <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          style={customStyles}
          ariaHideApp={false}
        >
          <ReceiptNotMatchModal
            handleSave={handleIssueSave}
          />
        </Modal>
        <Modal
          isOpen={isDeclarationOpen}
          onRequestClose={() => setIsDeclarationOpen(false)}
          style={declarationStyles}
          ariaHideApp={false}
        >
          <div>
            <div className='text-lg font-bold mb-3'>ใบแจ้งรายละเอียด — ส่งให้ลูกค้า</div>
            <textarea
              readOnly
              value={buildDeclarationText() || 'ยังไม่ได้เลือกรายการ'}
              rows={Math.min(20, rowData.filter((o) => declarationCategories(o).length > 0).length * 3 + 3)}
              className='w-full p-2 border border-gray-300 rounded-md text-sm'
            />
            <div className='flex justify-end gap-2 mt-3'>
              <button
                className='px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md'
                onClick={() => { navigator.clipboard.writeText(buildDeclarationText()); toast.success('คัดลอกแล้ว'); }}
              >
                Copy
              </button>
              <button
                className='bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded-md'
                onClick={() => setIsDeclarationOpen(false)}
              >
                ปิด
              </button>
            </div>
          </div>
        </Modal>
        <div>
          <InputLabel id="ship-date-multi-select-label">รอบเรือ</InputLabel>
          <Select
            labelId="ship-date-multi-select-label"
            id="ship-date-multi-select"
            multiple
            value={shipDateIds}
            label="รอบเรือ"
            onChange={handleShipDateSelectChange}
            size="small"
            renderValue={(selected) => {
              if (selected.includes('All')) return 'ทั้งหมด';
              return selected.map(val => {
                if (val === 'None') return 'ไม่มีรอบเรือ';
                const found = shipDates.find(sd => sd.id === val);
                return found ? `${formatDate(found.shipDate)} ${found.isAir ? '✈️แอร์' : '🚢เรือ'}` : val;
              }).join(', ');
            }}
            style={{ minWidth: 200 }}
            >
            <MenuItem value={'All'}>
              <Checkbox checked={shipDateIds.includes('All')} />
              <ListItemText primary="ทั้งหมด" />
            </MenuItem>
            <MenuItem value={'None'}>
              <Checkbox checked={shipDateIds.includes('None')} />
              <ListItemText primary="ไม่มีรอบเรือ" />
            </MenuItem>
            {shipDates && shipDates.map((shipDate) => (
              <MenuItem key={shipDate.id} value={shipDate.id}>
                <Checkbox checked={shipDateIds.includes(shipDate.id)} />
                <ListItemText primary={<span>{formatDate(shipDate.shipDate)}{shipTypeBadge(shipDate.isAir)}</span>} />
              </MenuItem>
            ))}
          </Select>
        </div>
        <span className='mt-auto text-2xl'>
          {profileState?.advanceAmount > 0 ? (
            <div>
              <div>{`ยอดรวมที่เลือกทั้งหมด ${totalThAmount.toFixed(2)} บาท`}</div>
              <div className='text-lg text-blue-600 mt-1'>
                มียอดฝากไว้ (คงเหลือในระบบ): {profileState.advanceAmount.toFixed(2)} บาท
              </div>
              <div className='text-lg text-blue-600'>
                ยอดตัดที่ใช้กับรายการนี้: {advanceToApply.toFixed(2)} บาท
                {advanceToApply < profileState.advanceAmount ? ` (เหลือ ${(profileState.advanceAmount - advanceToApply).toFixed(2)} บาทหลังตัด)` : ''}
              </div>
              <div className='text-lg font-semibold text-green-600 mt-1'>
                ยอดที่ต้องชำระเพิ่ม (โอน/บัตร/สลิป): {netDueAfterAdvance.toFixed(2)} บาท
              </div>
            </div>
          ) : (
            <div>{`ยอดรวมที่เลือกทั้งหมด ${totalThAmount.toFixed(2)} บาท`}</div>
          )}
        </span>
      </div>
      <table className='min-w-full divide-y divide-gray-200 border border-gray-300 mt-2'>
        <thead className="bg-gray-50">
          <tr>
            <th className="cursor-pointer" onClick={toggleAllRows}>Select All</th>
            <th>Order Date</th>
            <th>Rate</th>
            <th>Link</th>
            <th>
              <div className="flex flex-col items-center gap-1">
                <span>Amount</span>
                <button 
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-0.5 px-2 rounded-full text-xs"
                  onClick={() => toggleColumn('amountSelected')}
                >
                  Toggle Column
                </button>
              </div>
            </th>
            <th>
              <div className="flex flex-col items-center gap-1">
                <span>Delivery Fee Jp</span>
                <button 
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-0.5 px-2 rounded-full text-xs"
                  onClick={() => toggleColumn('deliveryFeeJpSelected')}
                >
                  Toggle Column
                </button>
              </div>
            </th>
            <th>
              <div className="flex flex-col items-center gap-1">
                <span>Weight Price</span>
                <button 
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-0.5 px-2 rounded-full text-xs"
                  onClick={() => toggleColumn('weightPriceSelected')}
                >
                  Toggle Column
                </button>
              </div>
            </th>
            <th>
              <div className="flex flex-col items-center gap-1">
                <span>Delivery Fee Th</span>
                <button 
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-0.5 px-2 rounded-full text-xs"
                  onClick={() => toggleColumn('deliveryFeeThSelected')}
                >
                  Toggle Column
                </button>
              </div>
            </th>
            <th>รอบเรือ</th>
          </tr>
        </thead>
        <tbody>
          {rowData.map((order, index) => (
            <tr key={index}>
              <td className='text-center'>
                <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-0.5 px-2 rounded-full" onClick={() => selectAllInRow(index)}>Toggle Row</button>
              </td>
              <td className='text-center'>{order.orderDate}</td>
              <td className='text-center'>{order.productRate}</td>
              <td className='text-center'><a href={order.url} target="_blank" rel="noopener noreferrer">Link</a></td>
              <td 
                className='text-center'
                style={{
                  cursor: 'pointer',
                  backgroundColor:
                    order.productPayment === 'ชำระครบแล้ว'
                      ? '#d6ffdd'
                      : order.amountSelected === "full"
                      ? '#a8e1ff'
                      : order.amountSelected === "half"
                      ? '#fff9c4'
                      : 'white'
                }}
                onClick={() => toggleSelection(index, 'amountSelected')}
              >
                {order.productPayment == 'มัดจำ 50%' ? `${order.amount*order.qty} (${order.amount*order.qty/ 2})` : order.amount*order.qty}
                {getUnpaidExtraJpFeesThb(order) > 0 && (
                  <div className='text-xs text-amber-700'>
                    +ตรวจ {(order.extraJpFees || []).reduce((s, f) => s + (Number(f.amountJpy) || 0), 0)}
                  </div>
                )}
              </td>
              <td 
                className='text-center'
                style={{
                  cursor: 'pointer',
                  backgroundColor:
                    order.shipJpPaid === 'ชำระครบแล้ว'
                      ? '#d6ffdd'
                      : order.deliveryFeeJpSelected === "full"
                      ? '#a8e1ff'
                      : order.deliveryFeeJpSelected === "half"
                      ? '#fff9c4'
                      : 'white'
                }}
                onClick={() => toggleSelection(index, 'deliveryFeeJpSelected')}
              >
                {order.shipJpPaid == 'มัดจำ 50%' ? `${order.deliveryFeeJp} (${order.deliveryFeeJp/ 2})` : order.deliveryFeeJp}
              </td>
              <td 
                className='text-center'
                style={{ cursor: 'pointer' ,backgroundColor: order.weightPaid ? '#d6ffdd' : (order.weightPriceSelected ? '#a8e1ff' : 'white')}}
                onClick={() => toggleSelection(index, 'weightPriceSelected')}
              >
                {order.weightPrice || (order.weight && order.weightRate ? 
                  (order.isSendOnly && order.weight < 1 ? order.weightRate : order.weight * order.weightRate) 
                  : null)}
                <WeightAssistTag status={weightAssists[order.id]} />
              </td>
              <td 
                className='text-center'
                style={{ cursor: 'pointer',backgroundColor: order.shipThPaid ? '#d6ffdd' : (order.deliveryFeeThSelected ? '#a8e1ff' : 'white')}}
                onClick={() => toggleSelection(index, 'deliveryFeeThSelected')}
              >
                {order.deliveryFeeTh}
              </td>
              <td className='text-center'>
                {order.ShipDate && (
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {formatDate(order.ShipDate.shipDate)}{shipTypeBadge(order.ShipDate.isAir)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className='flex justify-between mt-2'>
        <div>
          <div className='col-span-1 mt-2 my-1.5'>
            <button className='btn-primary' onClick={handlePastingOpenModal}>Paste Receipt</button>
            {/* <label htmlFor='file-upload-ship-payment' className='btn-primary py-3'>Upload Receipt</label>
            <input className="hidden" id="file-upload-ship-payment" type="file" accept="image/*" multiple onChange={handleFileChange} value="" />
            {files && <span className='ml-3'>{files.length} file is selected</span>} */}
            <div className='mt-3'>
              {files && files.length > 0 ? (
                files.map((file, index) => (
                  <div key={index}>{file.name}</div>
                ))
              ) : (
                <div>No files chosen</div>
              )}
            </div>
          </div>
        </div>
        <div>
          <div className='flex gap-2 ml-auto'>
            <button
              type='button'
              className='px-4 py-2 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
              disabled={!hasAnySelection(rowData)}
              onClick={() => setIsDeclarationOpen(true)}
            >
              ใบแจ้งรายละเอียด
            </button>
            <button className='btn-primary' disabled={isLoading} onClick={handleUpdatePayment}>{canSettleWithAdvance ? 'ตัดยอดด้วยยอดฝากไว้ (ไม่ต้องแนบสลิป)' : 'Update Payment'}</button>
          </div>
          <FormControlLabel 
            control={
            <Checkbox 
              checked={isCreditPaid}
              onChange={handleCheckboxChange} 
              />} 
            label="ลูกค้าจ่ายบัตรเครดิต" 
          />
        </div>
      </div>

      {/* Break line */}
      <hr className="my-4 border-gray-300" />

      {/* Section 2: Code lookup (non-company only) */}
      {!profileState?.isCompanyCustomer && (
        <div className='flex items-center gap-2 mb-2'>
          <TextField
            label="ค้นหาด้วย Code"
            size="small"
            value={codeInput}
            onChange={e => setCodeInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCodeLookup()}
            placeholder="เช่น PY260528A4B"
            style={{ width: 200 }}
          />
          <button className='btn-primary' disabled={isLoading} onClick={handleCodeLookup}>
            โหลด
          </button>
        </div>
      )}

      {/* Section 3: Payment Intent Button (Center) */}
      <div className='flex justify-center mb-2'>
        <div className='flex flex-col items-center gap-2'>
          <button
            className='btn-primary px-6 py-2 text-lg'
            disabled={isLoading}
            onClick={handlePaymentIntent}
          >
            ขอสรุปค่าใช้จ่าย
          </button>
        </div>
      </div>

      {/* Section 4: Payment TextArea */}
      {showPaymentTextArea && profileState?.isCompanyCustomer && orders && orders.length > 0 && testOrderSummary && (
        <div className="mt-6" ref={paymentTextAreaRef}>
          {bankTransferOnly && <div className='mb-2 p-2 rounded bg-red-100 text-red-700 font-bold'>{COMPANY_BANK_NOTICE}</div>}
          <PaymentTextArea
            orders={orders}
            orderSummary={testOrderSummary}
            smileName={smileName}
            advanceAmount={advanceToApply || 0}
            preGeneratedPaymentData={testOrderSummary}
          />
        </div>
      )}
      {showPaymentTextArea && !profileState?.isCompanyCustomer && nonCompanySummary && (
        <div className="mt-6" ref={paymentTextAreaRef}>
          <PaymentTextArea
            isNonCompany
            nonCompanySummary={nonCompanySummary}
            advanceAmount={advanceToApply || 0}
          />
        </div>
      )}
      
      <ReceiptPasteModal isOpen={isPastingModalOpen} onRequestClose={handlePastingCloseModal} onConfirm={handleConfirmPastedImage}  />
      {isLoading && <CircularProgress />}
    </div>
  )}

export default ShipDatePayment